import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Grid,
  Stack,
  IconButton,
  Button,
  Skeleton,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Download
} from '@mui/icons-material';
import { TransactionService } from '../../services';
import { useSelector } from 'react-redux';
import { selectSelectedTeam } from '../../store/authSlice';
import { fDateTime, toast } from '../../utils';
const ITEMS_PER_PAGE = 10;

const TransactionsTab = () => {
  const selectedTeam = useSelector(selectSelectedTeam);
  const [loading, setLoading] = useState(true);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [transactionsData, setTransactionsData] = useState([]);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: ITEMS_PER_PAGE
  });

  const fetchTransactions = useCallback(async (page = 1) => {
    if (!selectedTeam?.teamId) {
      setTransactionsData([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const teamParams = { 
        teamId: selectedTeam?.teamId, 
        teamType: selectedTeam?.teamType,
        page: page, 
        limit: ITEMS_PER_PAGE,
        filterRfid: !showAllTransactions
      };
      
      const response = await TransactionService.getAllTransactions(teamParams);
      
      if (response.success && response.data?.data) {
        // Use the API response data directly (don't slice it locally)
        const transactionsFromAPI = response.data.data.transactions || [];
        const totalRecords = response.data.data.totalRecord || 0;
        setTransactionsData(transactionsFromAPI);
        
        setPagination(prev => ({
          ...prev,
          currentPage: page,
          totalItems: totalRecords,
          totalPages: Math.ceil(totalRecords / ITEMS_PER_PAGE)
        }));
      } else {
        console.log('No data in response or unsuccessful');
        setTransactionsData([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactionsData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTeam?.teamId, selectedTeam?.teamType, showAllTransactions]);

  useEffect(() => {
    if (selectedTeam?.teamId) {
      fetchTransactions(1);
    } else {
      setLoading(false);
    }
  }, [selectedTeam?.teamId, fetchTransactions]);

  const handlePageChange = (newPage) => {
    fetchTransactions(newPage);
  };

  const handleShowAllChange = (event) => {
    setShowAllTransactions(event.target.checked);
  };

  const getExcelFileName = (contentDisposition) => {
    if (!contentDisposition) {
      return `transactions_${selectedTeam?.teamId || 'team'}.xlsx`;
    }

    const fileNameMatch = contentDisposition.match(
      /filename\*?=(?:UTF-8''|")?([^;"\n]+)/i
    );
    const fileName = fileNameMatch?.[1]?.replace(/"/g, '')?.trim();
    return fileName || `transactions_${selectedTeam?.teamId || 'team'}.xlsx`;
  };

  const handleDownloadExcel = async () => {
    if (!selectedTeam?.teamId) {
      toast.error('Please select a team first');
      return;
    }

    setDownloadingExcel(true);
    try {
      const response = await TransactionService.downloadTransactionsExcel({
        teamId: selectedTeam.teamId,
        filterRfid: !showAllTransactions
      });

      if (!response.success || !response.data) {
        toast.error(response.message || 'Failed to download transactions Excel');
        return;
      }

      const contentDisposition = response.headers?.['content-disposition'];
      const fileName = getExcelFileName(contentDisposition);
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);

      const fileUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = fileUrl;
      anchor.download = decodeURIComponent(fileName);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(fileUrl);

      toast.success('Transactions Excel downloaded successfully');
    } catch (error) {
      console.error('Error downloading transactions Excel:', error);
      toast.error('Error downloading transactions Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  // Skeleton component for transaction card loading state
  const TransactionCardSkeleton = () => (
    <Card 
      sx={{ 
        mb: 2,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        {/* Header with Charger Name and Status */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Skeleton variant="text" width={150} height={28} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Stack>

        {/* Connector Type */}
        <Skeleton variant="text" width={120} height={20} sx={{ mb: 2 }} />

        {/* Stats Grid */}
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Box>
              <Skeleton variant="text" width={90} height={16} />
              <Skeleton variant="text" width={60} height={24} />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box>
              <Skeleton variant="text" width={80} height={16} />
              <Skeleton variant="text" width={70} height={24} />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box>
              <Skeleton variant="text" width={70} height={16} />
              <Skeleton variant="text" width={100} height={24} />
              <Skeleton variant="text" width={70} height={16} sx={{ mt: 1 }} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const TransactionsSkeleton = () => (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Header skeleton */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Skeleton variant="text" width={180} height={32} />
        </Box>
      </Box>
      
      {/* Multiple transaction card skeletons */}
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
        <TransactionCardSkeleton key={item} />
      ))}
      
      {/* Pagination skeleton */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={60} height={20} />
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return <TransactionsSkeleton />;
  }

  // Show message if no team is selected
  if (!selectedTeam?.teamId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', p: 3 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Select a Team or Company
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please select a team or company from the dropdown above to view transactions
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Header with pagination info */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ lineHeight: 1.25 }}>
            Transactions ({pagination.totalItems})
          </Typography>

          <Button
            variant="contained"
            size="small"
            startIcon={<Download />}
            onClick={handleDownloadExcel}
            disabled={downloadingExcel || !selectedTeam?.teamId}
            sx={{
              whiteSpace: 'nowrap',
              flexShrink: 0,
              borderRadius: 2,
              px: 1.5
            }}
          >
            {downloadingExcel ? 'Downloading' : 'Export'}
          </Button>
        </Stack>

        <FormControlLabel
          control={
            <Switch
              checked={showAllTransactions}
              onChange={handleShowAllChange}
              size="small"
            />
          }
          label="Show all"
          sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
        />
      </Box>

      {transactionsData.length === 0 && !loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No transactions found
          </Typography>
        </Box>
      ) : (
        transactionsData.map((txn) => (
        <Card 
          key={txn.transactionId}
          sx={{ 
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            {/* Header with Charger Name and Status */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography 
                variant="h6" 
                fontWeight="600"
                color="text.primary"
              >
                {txn?.chargepoint_id}
              </Typography>
              <Chip 
                label={txn?.stop_meter_value === null ? 'Ongoing' : 'Completed'}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  ...(txn?.stop_meter_value === null ? {
                    backgroundColor: '#2196f3',
                    color: 'white'
                  } : {
                    backgroundColor: '#e0e0e0',
                    color: 'text.secondary'
                  })
                }}
              />
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={4}>
            {/* Transaction ID */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Transaction ID: {txn?.transactionId}
            </Typography>
            </Grid>

            {/* Connector ID */}
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connector: {txn?.connectorid}
              </Typography>
            </Grid>
            </Grid>

            {/* Stats Grid */}
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Energy Consumed
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color="text.primary">
                    {Math.abs(
                      txn?.stop_meter_value - txn?.start_meter_value
                    ) / 1000 || 0} kWh
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Charging Cost
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color="text.primary">
                    {txn?.totalAmountCharged}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Start Time
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color="text.primary" ml={2}>
                    {fDateTime(txn?.starttime, "yyyy-MM-dd hh:mm a")}
                  </Typography>
                  { txn?.stoptime &&
                    <>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Stop Time
                    </Typography>
                    <Typography variant="body1" fontWeight="600" color="text.primary">
                      {fDateTime(txn?.stoptime, "yyyy-MM-dd hh:mm a")}
                    </Typography>
                    </>
                  }
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        ))
      )}
      
      
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
        {pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              size="small"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1 || loading}
            >
              <ChevronLeft />
            </IconButton>
            
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: '60px', textAlign: 'center' }}>
              {pagination.currentPage} of {pagination.totalPages}
            </Typography>
            
            <IconButton 
              size="small"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages || loading}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TransactionsTab;